import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics Tracking ID - will be loaded from environment variable
const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID;

// Initialize Google Analytics
const initializeGA = () => {
  if (!GA_TRACKING_ID) {
    console.log('Google Analytics: No tracking ID configured');
    return false;
  }

  // Check if already loaded
  if (window.gtag) {
    return true;
  }

  // Load the GA script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  
  window.gtag('js', new Date());
  
  // Configure with anonymized IP for GDPR compliance
  window.gtag('config', GA_TRACKING_ID, {
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure',
    page_path: window.location.pathname,
  });

  console.log('Google Analytics: Initialized');
  return true;
};

// Disable Google Analytics
const disableGA = () => {
  if (!GA_TRACKING_ID) return;
  
  // Set opt-out cookie
  window[`ga-disable-${GA_TRACKING_ID}`] = true;
  
  // Remove existing cookies
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const name = cookie.split('=')[0].trim();
    if (name.startsWith('_ga') || name.startsWith('_gid') || name.startsWith('_gat')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    }
  }
  
  console.log('Google Analytics: Disabled and cookies removed');
};

// Track page views
const trackPageView = (path, title) => {
  if (!window.gtag || !GA_TRACKING_ID) return;
  
  window.gtag('config', GA_TRACKING_ID, {
    page_path: path,
    page_title: title,
  });
};

// Track custom events
export const trackEvent = (action, category, label, value) => {
  if (!window.gtag || !GA_TRACKING_ID) return;
  
  const consent = localStorage.getItem('cookie-consent');
  if (consent !== 'accepted') return;
  
  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

// Analytics component that manages tracking based on consent
const Analytics = () => {
  const location = useLocation();
  const [isEnabled, setIsEnabled] = useState(false);

  // Check consent and initialize/disable GA
  useEffect(() => {
    const checkConsent = () => {
      const consent = localStorage.getItem('cookie-consent');
      
      if (consent === 'accepted') {
        // User accepted all cookies - enable analytics
        const initialized = initializeGA();
        setIsEnabled(initialized);
      } else if (consent === 'declined' || consent === 'essential-only') {
        // User declined or only accepted essential - disable analytics
        disableGA();
        setIsEnabled(false);
      }
      // If no consent yet, don't do anything (wait for user choice)
    };

    // Initial check
    checkConsent();

    // Listen for storage changes (consent updates)
    const handleStorageChange = (e) => {
      if (e.key === 'cookie-consent') {
        checkConsent();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check periodically for same-tab updates
    const intervalId = setInterval(checkConsent, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  // Track page views when location changes
  useEffect(() => {
    if (isEnabled && GA_TRACKING_ID) {
      // Small delay to ensure page title is updated
      const timeoutId = setTimeout(() => {
        trackPageView(location.pathname + location.search, document.title);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [location, isEnabled]);

  // This component doesn't render anything
  return null;
};

export default Analytics;
